import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check } from "lucide-react";
import { useBooking } from "@/App";

const Section = ({ num, title, children }) => (
  <div className="py-8 border-b border-botanical/10 last:border-0">
    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2">{num}</p>
    <h3 className="font-serif text-2xl text-forest mb-4">{title}</h3>
    {children}
  </div>
);

const Chips = ({ items, testidPrefix }) => (
  <ul className="flex flex-wrap gap-2.5">
    {items.map((item, i) => (
      <li
        key={i}
        data-testid={`${testidPrefix}-${i}`}
        className="px-4 py-2 rounded-full bg-sagesurface/70 border border-botanical/10 text-sm font-medium text-inkbody"
      >
        {item}
      </li>
    ))}
  </ul>
);

export default function ProgramDetail({ program, onClose }) {
  const { openBooking } = useBooking();

  useEffect(() => {
    if (program) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [program]);

  return (
    <AnimatePresence>
      {program && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={`${program.name} details`}
          data-testid="program-detail-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-ivory w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[88vh] sm:rounded-3xl rounded-t-3xl overflow-y-auto shadow-hover"
          >
            <div className="relative h-60 sm:h-72 shrink-0">
              <img src={program.image} alt={`${program.name} — ${program.subtitle}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ivory via-forest/30 to-forest/20" />
              <button
                onClick={onClose}
                data-testid="program-detail-close-button"
                aria-label="Close program details"
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ivory/90 flex items-center justify-center text-forest hover:bg-ivory transition-colors"
              >
                <X size={20} />
              </button>
              <div className="absolute bottom-5 left-6 sm:left-10 right-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">{program.subtitle}</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-1">{program.name}</h2>
              </div>
            </div>

            <div className="px-6 sm:px-10 pb-10">
              <p className="font-serif italic text-xl text-botanical pt-7">"{program.tagline}"</p>

              <Section num="01" title="Who This Is For">
                <ul className="space-y-2.5">
                  {program.whoFor.map((w, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-inkmuted leading-relaxed">
                      <Check size={16} className="text-sage mt-0.5 shrink-0" />
                      {w}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section num="02" title="What We Focus On">
                <Chips items={program.focus} testidPrefix="program-focus-chip" />
              </Section>

              <Section num="03" title="What You Receive">
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {program.receive.map((r, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-inkmuted leading-relaxed">
                      <Check size={15} className="text-gold mt-0.5 shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section num="04" title="How Coaching Works">
                <ol className="space-y-3">
                  {program.howItWorks.map((s, i) => (
                    <li key={i} className="flex items-start gap-4 text-sm text-inkmuted leading-relaxed">
                      <span className="font-serif italic text-xl text-gold shrink-0 w-7">{i + 1}.</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </Section>

              <Section num="05" title="What Transformation Can Look Like">
                <Chips items={program.transformation} testidPrefix="program-transformation-chip" />
              </Section>

              <Section num="06" title="Questions, Answered">
                <div className="space-y-3">
                  {program.faq.map((f, i) => (
                    <details
                      key={i}
                      className="group bg-cardbg border border-botanical/10 rounded-2xl px-5 py-4 open:shadow-card"
                      data-testid={`program-faq-${i}`}
                    >
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-sm font-bold text-forest">
                        {f.q}
                        <span className="text-gold text-lg group-open:rotate-45 transition-transform duration-300">+</span>
                      </summary>
                      <p className="mt-3 text-sm text-inkmuted leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </Section>

              <div className="pt-8">
                <p className="text-xs text-inkmuted/80 leading-relaxed italic mb-6">{program.disclaimer}</p>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    onClose();
                    openBooking();
                  }}
                  data-testid="program-detail-book-button"
                  className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-forest text-ivory px-8 py-4 text-sm font-bold tracking-wide hover:bg-botanical transition-colors"
                >
                  BOOK YOUR FREE 1:1 DISCOVERY CALL <ArrowRight size={16} />
                </motion.button>
                <p className="text-center text-xs text-inkmuted mt-4">
                  Not sure if this is your pathway? That's okay — we'll figure it out together on the call.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
